import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import InputField from "@/components/forms/input_field";
import ButtonMain from "@/components/buttons/button_main";
import ButtonGhost from "@/components/buttons/button_ghost";

// Quick presets keep the common cases one click away.
const CHIPS = [
  { label: "Under $1,500", min: null, max: 1500 },
  { label: "$1,500 to $2,500", min: 1500, max: 2500 },
  { label: "$2,500+", min: 2500, max: null },
];

// Small anchored popover for the "Add budget" segment of the nav search pill.
// Writes to the shared FiltersContext so every deck, grid and map filters.
export default function BudgetPopover({ open, onClose, budget, onApply, onClear }) {
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const panelRef = useRef(null);

  // Re-seed the inputs from the live value each time the popover opens.
  useEffect(() => {
    if (!open) return;
    setMin(budget?.min ? String(budget.min) : "");
    setMax(budget?.max ? String(budget.max) : "");
  }, [open, budget]);

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    }
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  function apply(nextMin, nextMax) {
    onApply({ min: nextMin, max: nextMax });
    onClose();
  }

  const chipActive = (c) => (budget?.min ?? null) === c.min && (budget?.max ?? null) === c.max;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: -6, scale: 0.98, x: "-50%" }}
          animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
          exit={{ opacity: 0, y: -6, scale: 0.98, x: "-50%" }}
          transition={{ duration: 0.16 }}
          role="dialog"
          aria-label="Set your monthly budget"
          className="absolute left-1/2 top-full z-50 mt-3 w-[320px] rounded-2xl border border-night-line bg-night-surface p-5 shadow-ambient"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm font-semibold text-night-text">Monthly budget</p>
          <p className="mt-0.5 text-xs text-night-sub">Homes outside this range are hidden everywhere.</p>

          <div className="mt-3 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={() => apply(c.min, c.max)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  chipActive(c)
                    ? "border-brand-primary bg-night-tint text-brand-primary"
                    : "border-night-line bg-night-surface text-night-sub hover:border-night-text hover:text-night-text"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <InputField
              label="Min"
              type="number"
              min="0"
              placeholder="1,500"
              value={min}
              onChange={(e) => setMin(e.target.value)}
            />
            <InputField
              label="Max"
              type="number"
              min="0"
              placeholder="2,500"
              value={max}
              onChange={(e) => setMax(e.target.value)}
            />
          </div>

          <div className="mt-4 flex gap-2">
            <ButtonGhost
              full
              onClick={() => {
                onClear();
                onClose();
              }}
            >
              Clear
            </ButtonGhost>
            <ButtonMain full onClick={() => apply(Number(min) || null, Number(max) || null)}>
              Apply
            </ButtonMain>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
